import { FileText, ImageSquare, VideoCamera } from "@phosphor-icons/react";
import cx from "classnames";

import {
  interactiveSurfaceClasses,
  interactiveSurfaceIconClasses,
} from "../../../components/InteractiveSurface";
import { type ProjectFeedAttachment } from "../../../services/askPunttProjectFeed.types";

/**
 * A single attachment row inside a feed post: type icon + filename + a
 * version badge (`v1` / `v2` / `v3`). When `onOpen` is provided the whole row
 * is a button that opens the file's preview / version history.
 */
export function FeedAttachmentRow({
  attachment,
  onOpen,
}: {
  attachment: ProjectFeedAttachment;
  onOpen?: (attachment: ProjectFeedAttachment) => void;
}) {
  const TypeIcon =
    attachment.type === "image"
      ? ImageSquare
      : attachment.type === "video"
        ? VideoCamera
        : FileText;

  const content = (
    <>
      <span
        aria-hidden="true"
        className={interactiveSurfaceIconClasses(
          "flex size-8 shrink-0 items-center justify-center rounded-md bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11",
        )}
      >
        <TypeIcon size={16} />
      </span>
      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-puntt-neutral-gray-12">
        {attachment.name}
      </span>
      {attachment.versionNumber != null && (
        <span
          className="font-mono shrink-0 rounded border border-puntt-neutral-gray-7 bg-base-white px-1.5 py-px text-[10px] font-medium text-puntt-neutral-gray-11"
          data-testid={`feed-attachment-version-${attachment.fileId}`}
        >
          v{attachment.versionNumber}
        </span>
      )}
    </>
  );

  const staticClassName =
    "flex w-full items-center gap-2.5 rounded-lg border border-puntt-neutral-gray-5 bg-base-white px-3 py-2 text-left";

  if (onOpen) {
    return (
      <button
        type="button"
        onClick={() => onOpen(attachment)}
        aria-label={`Open ${attachment.name}${
          attachment.versionNumber != null
            ? ` version ${attachment.versionNumber}`
            : ""
        }`}
        data-testid={`feed-attachment-row-${attachment.fileId}`}
        className={cx(
          "flex w-full items-center gap-2.5 px-3 py-2 text-left",
          interactiveSurfaceClasses({ interactive: true }),
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      data-testid={`feed-attachment-row-${attachment.fileId}`}
      className={staticClassName}
    >
      {content}
    </div>
  );
}
